'use client';

import { useUiContent } from '@/components/ui-content-provider';
import { LocaleDetails } from 'shared/src/types/languages.type';

// Duck-typed instead of importing firebase/firestore's Timestamp class: this
// component is reachable from the always-loaded header, and a runtime Timestamp
// import pulls the whole Firestore SDK into the initial bundle.
type TimestampLike = { seconds?: number; nanoseconds?: number; toDate?: () => Date };

export const LocaleDate = ({ date, lang }: { date: TimestampLike | number | string | any; lang: LocaleDetails }) => {
  const ui = useUiContent();
  let dateObject: Date;

  // Convert various date formats to Date object
  if (typeof date === 'number') {
    // If date is a number (milliseconds since epoch), convert to Date
    dateObject = new Date(date);
  } else if (typeof date === 'string') {
    // If date is a string, convert it to a Date
    dateObject = new Date(date);
  } else if (typeof date === 'object' && date !== null && typeof date.toDate === 'function') {
    // A live Firestore Timestamp (or anything Timestamp-like)
    dateObject = date.toDate();
  } else if (
    typeof date === 'object' &&
    date !== null &&
    (('seconds' in date && 'nanoseconds' in date) || ('_seconds' in date && '_nanoseconds' in date))
  ) {
    // A serialized Timestamp ({seconds, nanoseconds} is what survives the RSC boundary)
    const seconds = date.seconds ?? date._seconds ?? 0;
    const nanoseconds = date.nanoseconds ?? date._nanoseconds ?? 0;
    dateObject = new Date(seconds * 1000 + Math.floor(nanoseconds / 1_000_000));
  } else {
    return null;
  }

  // Check if the date is valid
  if (isNaN(dateObject.getTime())) {
    return null;
  }

  const formatDate = (): string => {
    const dateString = dateObject.toLocaleDateString(lang.path, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    const now = new Date();

    if (dateObject > now) {
      // For future dates, use 'publishingOn' from uiContent
      return `${ui.publishingOn} ${dateString}`;
    } else {
      // For past or current dates, use 'publishedOn' from uiContent
      return `${ui.publishedOn} ${dateString}`;
    }
  };

  // Usage
  const result = formatDate();

  return (
    <p className="pb-1 text-end text-xs max-sm:text-xs max-xxs:text-[0.7rem]">
      {result}
    </p>
  );
};
